/**
 * Pure grouping/ordering helpers for the checklist screen — kept separate from the components so
 * the row-ordering rules can be unit tested without rendering anything.
 */
import type { ChecklistAccount, ChecklistEntry } from './checklist';

/**
 * `task_def.category` values (see `reference/tasks.json`), in WP5-spec display order: DAILY_*
 * first, then WEEKLY_*, GUILD, LEGION. The catalog has no literal `MONTHLY` category — the seed's
 * one monthly-cadence task is categorized `OTHER` — so `OTHER` is placed last to satisfy the
 * spec's "MONTHLY last" (see WP5 Deviations).
 */
export const CATEGORY_ORDER = [
  'DAILY_QUEST',
  'DAILY_CONTENT',
  'WEEKLY_QUEST',
  'WEEKLY_CONTENT',
  'GUILD',
  'LEGION',
  'OTHER',
] as const;

/** User-defined tasks have no catalog category; grouped on their own, after everything else. */
export const CUSTOM_CATEGORY = 'CUSTOM';

export const CATEGORY_LABELS: Record<string, string> = {
  DAILY_QUEST: 'Daily Quests',
  DAILY_CONTENT: 'Daily Content',
  WEEKLY_QUEST: 'Weekly Quests',
  WEEKLY_CONTENT: 'Weekly Content',
  GUILD: 'Guild',
  LEGION: 'Legion',
  OTHER: 'Other & Monthly',
  [CUSTOM_CATEGORY]: 'Custom Tasks',
};

export function categoryRank(category: string): number {
  const index = CATEGORY_ORDER.indexOf(category as (typeof CATEGORY_ORDER)[number]);
  return index >= 0 ? index : CATEGORY_ORDER.length;
}

export function categoryOfEntry(entry: ChecklistEntry, taskCategoryBySlug: Map<string, string>): string {
  if (entry.source === 'CUSTOM' || entry.taskDefSlug === null) {
    return CUSTOM_CATEGORY;
  }
  return taskCategoryBySlug.get(entry.taskDefSlug) ?? 'OTHER';
}

export interface ChecklistRow {
  entry: ChecklistEntry;
  ownerLabel: string;
  category: string;
}

/** Flattens one account's (account-scoped + every character's) tasks into single sortable rows. */
export function buildRows(
  account: ChecklistAccount,
  taskCategoryBySlug: Map<string, string>,
): ChecklistRow[] {
  const rows: ChecklistRow[] = [];
  for (const entry of account.tasks) {
    rows.push({ entry, ownerLabel: 'Account', category: categoryOfEntry(entry, taskCategoryBySlug) });
  }
  for (const character of account.characters) {
    for (const entry of character.tasks) {
      rows.push({
        entry,
        ownerLabel: character.name,
        category: categoryOfEntry(entry, taskCategoryBySlug),
      });
    }
  }
  return rows.sort((a, b) => {
    const rankDiff = categoryRank(a.category) - categoryRank(b.category);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    return a.entry.name.localeCompare(b.entry.name) || a.ownerLabel.localeCompare(b.ownerLabel);
  });
}

export interface ChecklistGroup {
  category: string;
  label: string;
  rows: ChecklistRow[];
}

export function groupRows(rows: ChecklistRow[]): ChecklistGroup[] {
  const buckets = new Map<string, ChecklistRow[]>();
  for (const row of rows) {
    const bucket = buckets.get(row.category);
    if (bucket) {
      bucket.push(row);
    } else {
      buckets.set(row.category, [row]);
    }
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => categoryRank(a) - categoryRank(b))
    .map(([category, groupRowsForCategory]) => ({
      category,
      label: CATEGORY_LABELS[category] ?? category,
      rows: groupRowsForCategory,
    }));
}

/** Clamps a counter delta to `[0, maxCount]` — used for both the +/- stepper and its tests. */
export function clampCount(count: number, delta: number, maxCount: number): number {
  return Math.min(maxCount, Math.max(0, count + delta));
}

/**
 * Cadence display order for the mobile "today" view: WEEKLY and MONTHLY content is rarer and more
 * costly to forget than a daily (which simply resets tomorrow), so it leads — a judgment call
 * documented in WP5 Deviations since the spec's "biggest-impact groups first" doesn't define impact.
 */
export const TODAY_CADENCE_ORDER: ReadonlyArray<ChecklistEntry['cadence']> = ['WEEKLY', 'MONTHLY', 'DAILY'];

export function cadenceRank(cadence: ChecklistEntry['cadence']): number {
  const index = TODAY_CADENCE_ORDER.indexOf(cadence);
  return index >= 0 ? index : TODAY_CADENCE_ORDER.length;
}
